"use client"

import {
  Heart,
  User,
  ListMusic,
  Play,
  Download,
  Music2,
} from "lucide-react"
import { createPortal } from "react-dom"
import { motion, AnimatePresence } from "framer-motion"

type Props = {
  open: boolean

  song: {
    songName: string
    songArtist: string
    songimg?: string
  }

  isFavorite: boolean
  isOfflineSaved: boolean
  isSavingOffline: boolean

  onPlay: () => void
  onArtist: () => void
  onPlaylist: () => void
  onFavorite: () => void
  onOffline: () => void
  onClose: () => void
}

export default function TrackContextMenu({
  open,
  song,
  isFavorite,
  isOfflineSaved,
  isSavingOffline,
  onPlay,
  onArtist,
  onPlaylist,
  onFavorite,
  onOffline,
  onClose,
}: Props) {
  if (typeof document === "undefined") {
    return null
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          {/* =====================================================
              BACKDROP
          ===================================================== */}

          <motion.div
            className="
              fixed
              inset-0
              z-[2147483646]
              bg-black/45
              backdrop-blur-[3px]
            "
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.22,
              ease: [0.22, 1, 0.36, 1],
            }}
            onPointerDown={(event) => {
              event.preventDefault()
              event.stopPropagation()
            }}
            onMouseDown={(event) => {
              event.preventDefault()
              event.stopPropagation()
            }}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()

              onClose()
            }}
          />

          {/* =====================================================
              DRAWER
          ===================================================== */}

          <motion.div
            className="
              fixed
              inset-x-0
              bottom-0
              z-[2147483647]
              mx-auto
              w-full
              max-w-2xl
              overflow-hidden
              rounded-t-[1.7rem]
              border
              border-b-0
              border-white/[0.10]
              bg-[#111111]/[0.97]
              shadow-[0_-20px_70px_rgba(0,0,0,0.65)]
              backdrop-blur-3xl
              pb-[env(safe-area-inset-bottom)]
              touch-none
            "
            initial={{
              y: "100%",
              opacity: 0.8,
            }}
            animate={{
              y: 0,
              opacity: 1,
            }}
            exit={{
              y: "100%",
              opacity: 0,
            }}
            transition={{
              type: "spring",
              stiffness: 420,
              damping: 38,
              mass: 0.8,
            }}
            drag="y"
            dragConstraints={{
              top: 0,
              bottom: 0,
            }}
            dragElastic={{
              top: 0.02,
              bottom: 0.55,
            }}
            onDragEnd={(_, info) => {
              const shouldClose =
                info.offset.y > 100 ||
                info.velocity.y > 650

              if (shouldClose) {
                onClose()
              }
            }}
            onPointerDown={(event) => {
              event.stopPropagation()
            }}
            onMouseDown={(event) => {
              event.stopPropagation()
            }}
            onClick={(event) => {
              event.stopPropagation()
            }}
          >
            {/* =================================================
                HANDLE
            ================================================= */}

            <div
              className="
                flex
                h-8
                w-full
                items-center
                justify-center
              "
            >
              <div
                className="
                  h-1
                  w-10
                  rounded-full
                  bg-white/20
                "
              />
            </div>

            {/* =================================================
                HEADER
            ================================================= */}

            <div
              className="
                flex
                items-center
                gap-4
                px-5
                pb-5
              "
            >
              {/* COVER */}

              <div
                className="
                  relative
                  h-16
                  w-16
                  shrink-0
                  overflow-hidden
                  rounded-[1rem]
                  bg-white/[0.06]
                  shadow-lg
                "
              >
                {song.songimg ? (
                  <img
                    src={song.songimg}
                    alt={song.songName}
                    className="
                      h-full
                      w-full
                      object-cover
                    "
                  />
                ) : (
                  <div
                    className="
                      flex
                      h-full
                      w-full
                      items-center
                      justify-center
                      text-white/30
                    "
                  >
                    <Music2 size={25} />
                  </div>
                )}

                {/* subtle overlay */}

                <div
                  className="
                    pointer-events-none
                    absolute
                    inset-0
                    bg-gradient-to-t
                    from-black/30
                    to-transparent
                  "
                />
              </div>

              {/* SONG INFO */}

              <div
                className="
                  min-w-0
                  flex-1
                "
              >
                <p
                  className="
                    truncate
                    text-[15px]
                    font-semibold
                    tracking-[-0.01em]
                    text-white
                  "
                >
                  {song.songName}
                </p>

                <p
                  className="
                    mt-1
                    truncate
                    text-[13px]
                    text-white/40
                  "
                >
                  {song.songArtist}
                </p>

                <div
                  className="
                    mt-2
                    flex
                    items-center
                    gap-1.5
                  "
                >
                  <span
                    className="
                      h-1.5
                      w-1.5
                      rounded-full
                      bg-white/40
                    "
                  />

                  <span
                    className="
                      text-[10px]
                      font-medium
                      uppercase
                      tracking-[0.12em]
                      text-white/25
                    "
                  >
                    Opciones
                  </span>
                </div>
              </div>

              {/* =================================================
                  RIGHT VISUAL
              ================================================= */}

              <div
                className="
                  hidden
                  h-16
                  w-24
                  shrink-0
                  items-center
                  justify-center
                  rounded-[1rem]
                  border
                  border-white/[0.06]
                  bg-white/[0.025]
                  sm:flex
                "
              >
                <div
                  className="
                    flex
                    h-8
                    items-center
                    gap-[3px]
                  "
                >
                  <WaveBar height="10px" />
                  <WaveBar height="17px" />
                  <WaveBar height="25px" />
                  <WaveBar height="14px" />
                  <WaveBar height="21px" />
                  <WaveBar height="29px" />
                  <WaveBar height="17px" />
                  <WaveBar height="11px" />
                </div>
              </div>
            </div>

            {/* =================================================
                SEPARATOR
            ================================================= */}

            <div
              className="
                mx-5
                h-px
                bg-white/[0.06]
              "
            />

            {/* =================================================
                ACTIONS
            ================================================= */}

            <div
              className="
                grid
                grid-cols-2
                gap-2
                p-4
                sm:p-5
              "
            >
              <MenuButton
                icon={<Play size={18} />}
                label="Reproducir"
                onClick={onPlay}
              />

              <MenuButton
                icon={<User size={18} />}
                label="Ir al artista"
                onClick={onArtist}
              />

              <MenuButton
                icon={<ListMusic size={18} />}
                label="Ir a la lista"
                onClick={onPlaylist}
              />

              <MenuButton
                icon={
                  <Heart
                    size={18}
                    className={
                      isFavorite
                        ? "fill-red-500 text-red-500"
                        : "text-white/70"
                    }
                  />
                }
                label={
                  isFavorite
                    ? "Quitar favorito"
                    : "Añadir favorito"
                }
                onClick={onFavorite}
              />

              <MenuButton
                icon={
                  <Download
                    size={18}
                    className={
                      isOfflineSaved
                        ? "text-blue-400"
                        : "text-white/70"
                    }
                  />
                }
                label={
                  isOfflineSaved
                    ? "Guardada offline"
                    : isSavingOffline
                      ? "Descargando..."
                      : "Guardar offline"
                }
                disabled={
                  isOfflineSaved ||
                  isSavingOffline
                }
                onClick={onOffline}
              />

              {/* BALANCE / CLOSE AREA */}

              <motion.button
                type="button"
                onClick={onClose}
                whileTap={{
                  scale: 0.97,
                }}
                className="
                  flex
                  min-h-[58px]
                  items-center
                  justify-center
                  gap-2
                  rounded-2xl
                  border
                  border-white/[0.06]
                  bg-white/[0.025]
                  px-3
                  text-sm
                  text-white/35
                  transition-colors
                  hover:bg-white/[0.05]
                  hover:text-white/55
                "
              >
                <span
                  className="
                    text-xs
                    uppercase
                    tracking-[0.12em]
                  "
                >
                  Desliza para cerrar
                </span>
              </motion.button>
            </div>

            {/* =================================================
                BOTTOM HINT
            ================================================= */}

            <div
              className="
                flex
                justify-center
                px-5
                pb-3
              "
            >
              <div
                className="
                  flex
                  items-center
                  gap-2
                  text-[10px]
                  font-medium
                  uppercase
                  tracking-[0.12em]
                  text-white/15
                "
              >
                <span
                  className="
                    h-px
                    w-5
                    bg-white/10
                  "
                />

                Arrastra hacia abajo

                <span
                  className="
                    h-px
                    w-5
                    bg-white/10
                  "
                />
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  )
}


/* ============================================================
   WAVE BAR
============================================================ */

function WaveBar({
  height,
}: {
  height: string
}) {
  return (
    <motion.span
      className="
        w-[3px]
        rounded-full
        bg-white/25
      "
      style={{
        height,
      }}
      animate={{
        scaleY: [0.65, 1, 0.7, 0.9, 0.65],
      }}
      transition={{
        duration: 1.4,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  )
}


/* ============================================================
   MENU BUTTON
============================================================ */

function MenuButton({
  icon,
  label,
  onClick,
  disabled = false,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <motion.button
      type="button"
      disabled={disabled}
      onClick={onClick}
      whileTap={
        disabled
          ? undefined
          : {
              scale: 0.97,
            }
      }
      className="
        group
        flex
        min-h-[58px]
        w-full
        items-center
        gap-3
        rounded-2xl
        border
        border-white/[0.06]
        bg-white/[0.025]
        px-4
        text-left
        transition-all
        duration-200
        hover:border-white/[0.10]
        hover:bg-white/[0.055]
        active:bg-white/[0.08]
        disabled:cursor-default
        disabled:opacity-35
      "
    >
      <span
        className="
          flex
          h-9
          w-9
          shrink-0
          items-center
          justify-center
          rounded-xl
          bg-white/[0.055]
          text-white/70
          transition-colors
          group-hover:bg-white/[0.08]
        "
      >
        {icon}
      </span>

      <span
        className="
          min-w-0
          truncate
          text-[13px]
          font-medium
          text-white/75
          transition-colors
          group-hover:text-white
        "
      >
        {label}
      </span>
    </motion.button>
  )
}